// All content is MOCKED for the frontend-only Infinity Solutions site.
// Company is a partner of VLink and shares the same offices/phones.

export const company = {
  name: "Infinity Solutions",
  tagline: "Accelerate Your Business with AI-Powered Solutions",
  email: "info@infinitysolutions.com",
  phone: "+1 (860) 247-1400",
};

export const navLinks = [
  { label: "About", to: "/about" },
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Case Studies", href: "#case-studies" },
  { label: "Technologies", href: "#technologies" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export const heroImage =
  "https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxBSSUyMGlubm92YXRpb258ZW58MHx8fHwxNzg4NTMwNzgxfDA&ixlib=rb-4.1.0&q=85";

export const trustedLogos = [
  "BlackRock",
  "Maruti Suzuki",
  "Volvo",
  "Checkwriters",
  "Rediker",
  "Stanley",
  "Schneider Electric",
];

export const stats = [
  { value: 14, suffix: "+", label: "Years in Business" },
  { value: 30, suffix: "+", label: "Industries Covered" },
  { value: 650, suffix: "+", label: "Tech Evangelists" },
  { value: 8, suffix: "+", label: "Global Offices" },
  { value: 12, suffix: "+", label: "Countries We Serve" },
  { value: 300, suffix: "+", label: "Success Stories" },
];

export const services = [
  {
    icon: "Brain",
    title: "AI-Powered Solutions & Consulting",
    desc: "Deliver smarter experiences, automate tasks, and make better decisions with AI-driven product development using machine learning, NLP, and computer vision.",
  },
  {
    icon: "Cpu",
    title: "Digital Engineering Services",
    desc: "Solutions that combine AI, cloud, and microservices to modernize legacy systems, streamline operations, and build secure, next-gen architectures.",
  },
  {
    icon: "Code2",
    title: "Custom Software Development",
    desc: "Tailored software integrated with AI-powered features such as chatbots, analytics, and automation to solve complex challenges across platforms.",
  },
  {
    icon: "Smartphone",
    title: "Mobile App Development",
    desc: "Cross-platform mobile apps with AI-driven features like voice recognition, predictive behavior, and personalized content for user-centric experiences.",
  },
  {
    icon: "Infinity",
    title: "DevOps",
    desc: "Automate, scale, and modernize product engineering to streamline operations, accelerate timelines, and improve overall product quality.",
  },
  {
    icon: "Users",
    title: "IT Staff Augmentation",
    desc: "Onboard the top 3% of pre-vetted talent within 48 hours — AI developers, engineers, and designers — to scale your team with speed and expertise.",
  },
  {
    icon: "ShieldCheck",
    title: "Cybersecurity",
    desc: "Comprehensive cybersecurity services to enhance your security posture, mitigate risks, and safeguard critical business assets with tailored solutions.",
  },
  {
    icon: "Cloud",
    title: "Cloud Consulting",
    desc: "Scalable cloud consulting ensuring seamless integration, optimized performance, and secure access to your digital resources across environments.",
  },
];

export const industries = [
  "Real Estate", "Agriculture", "Telecom", "Manufacturing", "Automotive",
  "Healthcare", "Finance", "eCommerce", "Travel", "Media & Entertainment",
  "Education", "Logistics",
];

export const caseStudies = [
  {
    slug: "auto-parts-sales",
    tag: "Automotive",
    image: "https://images.unsplash.com/photo-1727893119356-1702fe921cf9",
    title: "Auto Parts Smart Sales App",
    desc: "Empowering sales teams with smart inventory & spare parts management.",
    metrics: [
      { value: "30%", label: "boost in sales productivity" },
      { value: "40%", label: "reduction in inventory overstock" },
    ],
    challenge: "A leading auto-parts distributor relied on paper catalogs and disconnected spreadsheets, leaving field sales teams unable to check live stock or pricing. Orders were slow, error-prone, and overstock was rising.",
    solution: "We built a mobile-first smart sales platform with real-time inventory, AI-driven part recommendations, and offline-ready order capture that syncs the moment connectivity returns.",
    results: [
      { value: "30%", label: "boost in sales productivity" },
      { value: "40%", label: "less inventory overstock" },
      { value: "2x", label: "faster order processing" },
      { value: "98%", label: "order accuracy" },
    ],
    approach: ["Discovery & field research", "AI recommendation engine", "Offline-first mobile app", "Live ERP integration"],
    tech: ["React Native", "Node.js", "PostgreSQL", "Redis", "AWS"],
    quote: { text: "Our reps finally have everything they need in their pocket. Orders that took a day now take minutes.", author: "VP of Sales, Auto-Parts Distributor" },
  },
  {
    slug: "legacy-modernization",
    tag: "Finance",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab",
    title: "Modernizing Legacy IT Infrastructure",
    desc: "Transform outdated backend systems into agile, digital-ready platforms.",
    metrics: [
      { value: "45%", label: "more efficient operations" },
      { value: "35%", label: "lower IT maintenance costs" },
    ],
    challenge: "A financial services firm was running critical operations on a decades-old monolith that was expensive to maintain, risky to change, and impossible to scale for new digital products.",
    solution: "We executed an incremental strangler-fig migration to cloud-native microservices, keeping the business running while progressively retiring the legacy core.",
    results: [
      { value: "45%", label: "more efficient operations" },
      { value: "35%", label: "lower maintenance cost" },
      { value: "4x", label: "faster release cadence" },
      { value: "0", label: "downtime during migration" },
    ],
    approach: ["Architecture assessment", "Domain decomposition", "Incremental migration", "Observability & hardening"],
    tech: ["Java", "Kubernetes", "Kafka", "GraphQL", "Azure"],
    quote: { text: "They modernized our core without ever stopping the business. Our teams now ship in days, not quarters.", author: "CTO, Financial Services Firm" },
  },
  {
    slug: "smart-medical-records",
    tag: "Healthcare",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef",
    title: "Smart Medical Records App",
    desc: "Enabling healthcare providers to streamline patient record management.",
    metrics: [
      { value: "50%", label: "less admin workload" },
      { value: "40%", label: "boost in patient engagement" },
    ],
    challenge: "Clinicians spent hours on manual charting and chasing records across systems, reducing time with patients and increasing burnout and errors.",
    solution: "We delivered a HIPAA-ready records platform with voice-to-text notes, smart summaries, and a patient portal, all integrated with existing EHR systems.",
    results: [
      { value: "50%", label: "less admin workload" },
      { value: "40%", label: "higher patient engagement" },
      { value: "60%", label: "faster charting" },
      { value: "100%", label: "compliance readiness" },
    ],
    approach: ["Clinical workflow mapping", "Secure EHR integration", "AI note summaries", "Patient portal"],
    tech: ["React", "Python", "FHIR", "MongoDB", "AWS"],
    quote: { text: "The AI summaries give me back hours every week — time I spend with patients instead of paperwork.", author: "Chief Medical Officer" },
  },
  {
    slug: "hyperlocal-shopping",
    tag: "eCommerce",
    image: "https://images.unsplash.com/photo-1664455340023-214c33a9d0bd",
    title: "Hyper-local Shopping App",
    desc: "Blending local retail perks with online convenience to compete with giants.",
    metrics: [
      { value: "40%", label: "increase in customer retention" },
      { value: "30%", label: "boost in upsell opportunities" },
    ],
    challenge: "Local retailers were losing customers to national eCommerce giants and had no unified way to offer online convenience with a neighborhood touch.",
    solution: "We launched a hyper-local marketplace with same-day delivery, personalized recommendations, and loyalty rewards that keep shoppers coming back.",
    results: [
      { value: "40%", label: "higher retention" },
      { value: "30%", label: "more upsell" },
      { value: "3x", label: "repeat purchase rate" },
      { value: "25%", label: "larger basket size" },
    ],
    approach: ["Marketplace platform", "Personalization engine", "Same-day logistics", "Loyalty program"],
    tech: ["Next.js", "Node.js", "Elasticsearch", "Stripe", "GCP"],
    quote: { text: "We finally compete with the big players — but with the local feel our customers love.", author: "Founder, Local Retail Network" },
  },
  {
    slug: "retail-shopping-app",
    tag: "Retail",
    image: "https://images.unsplash.com/photo-1551650975-87deedd944c3",
    title: "Retail Mobile Shopping App",
    desc: "Accelerating digital commerce with enterprise-grade scalability and compliance.",
    metrics: [
      { value: "60%", label: "increase in checkout completion" },
      { value: "45%", label: "rise in customer retention" },
    ],
    challenge: "A national retailer's aging app suffered from slow load times and a clunky checkout, causing high cart abandonment during peak sales events.",
    solution: "We rebuilt the shopping experience with a performant, scalable architecture, one-tap checkout, and AI-powered product discovery that handles seasonal spikes with ease.",
    results: [
      { value: "60%", label: "higher checkout completion" },
      { value: "45%", label: "better retention" },
      { value: "70%", label: "faster load times" },
      { value: "5M+", label: "peak concurrent users" },
    ],
    approach: ["Performance re-architecture", "One-tap checkout", "AI discovery", "Load & resilience testing"],
    tech: ["Flutter", "Node.js", "Redis", "Kubernetes", "AWS"],
    quote: { text: "Our biggest sale day ever ran flawlessly — checkout completion has never been higher.", author: "Head of Digital, National Retailer" },
  },
  {
    slug: "edtech-modernization",
    tag: "EdTech",
    image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655",
    title: "EdTech Platform Modernization",
    desc: "Modernizing a student information system into a cloud-ready platform.",
    metrics: [
      { value: "40%", label: "boost in engagement" },
      { value: "30%", label: "reduction in student dropouts" },
    ],
    challenge: "An education provider's student information system was slow, hard to maintain, and couldn't support modern remote-learning experiences.",
    solution: "We modernized the platform into a cloud-native system with adaptive learning paths, real-time dashboards, and integrations for content and communication.",
    results: [
      { value: "40%", label: "higher engagement" },
      { value: "30%", label: "fewer dropouts" },
      { value: "50%", label: "faster onboarding" },
      { value: "99.9%", label: "platform uptime" },
    ],
    approach: ["Platform modernization", "Adaptive learning engine", "Analytics dashboards", "Content integrations"],
    tech: ["React", "Python", "PostgreSQL", "Kubernetes", "Azure"],
    quote: { text: "Engagement is up and dropouts are down — the new platform genuinely changed outcomes for students.", author: "Director of Learning" },
  },
];

export const testimonials = [
  {
    name: "Sony George Chandy",
    role: "Consultant",
    text: "I have found the company management very approachable and they respond to all my requests in a very timely and professional manner.",
  },
  {
    name: "Carl W",
    role: "Technology Services Client",
    text: "I've been working closely with the team for quite some time. They couldn't be more accommodating and flexible. The team listens closely and makes great suggestions.",
  },
  {
    name: "Vipin Sumathi",
    role: "Consultant",
    text: "Overall the experience was great. This company stands out as an exceptional choice and comes highly recommended.",
  },
  {
    name: "Rick Brady",
    role: "Client Partner, State of CT",
    text: "They consistently go the extra mile to present only top-quality candidates. It saves me time and sets every project up for success.",
  },
  {
    name: "Milena Erwin",
    role: "Executive Director, CT Technology Council",
    text: "Everything delivered on time with excellent, responsive communication. Their efforts led to a noticeable boost in engagement.",
  },
  {
    name: "Andrew Anderlonis",
    role: "CTO at Checkwriters",
    text: "I've worked with several service providers over the years and Infinity Solutions comes out on top with a truly personalized experience.",
  },
];

export const whyChoose = {
  image:
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxzb2Z0d2FyZSUyMHRlYW18ZW58MHx8fHwxNzg4NTMwNzgxfDA&ixlib=rb-4.1.0&q=85",
  points: [
    "Strong partnerships with leading tech giants like AWS, Google & Microsoft",
    "Over 300+ successful projects delivered across various industries",
    "A team of 650+ expert developers with diverse skill sets",
    "Proven track record with 300+ satisfied clients globally",
    "Dedicated support with 24/7 customer service",
    "Commitment to scalable and secure solutions",
  ],
};

export const techStacks = {
  "Frontend": ["HTML", "CSS", "JavaScript", "React", "Angular", "Vue.js", "Next.js", "Ember"],
  "Backend": [".NET", "Java", "Python", "PHP", "Go", "Node.js"],
  "Mobile": ["Android", "iOS", "Flutter", "React Native", "Xamarin", "Ionic", "PWA"],
  "Databases": ["MySQL", "PostgreSQL", "Oracle", "Cassandra", "MongoDB", "Hive"],
  "Cloud": ["Amazon RDS", "Amazon Redshift", "Azure Cosmos DB", "Azure SQL", "Blob Storage"],
  "DevOps": ["Docker", "Kubernetes", "Terraform", "Ansible", "Chef", "Azure DevOps"],
};

export const processSteps = [
  { step: "01", title: "Consult Our Experts", desc: "Share your project requirements, and we'll respond within 24 hours." },
  { step: "02", title: "Strategic Consultation", desc: "Our experts collaborate with you to understand goals and craft a tailored strategy." },
  { step: "03", title: "Project Kickoff", desc: "We assign the right team, set milestones, and start building with full transparency." },
  { step: "04", title: "Delivery & Support", desc: "We deliver on time and provide continuous support for your long-term success." },
];

export const processImage =
  "https://images.pexels.com/photos/5833779/pexels-photo-5833779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const technologies = [
  "AI", "Machine Learning", "Generative AI", "Cloud", "Data Science", "IoT",
  "AR/VR", "Blockchain", "Business Intelligence", "AI Agents", "NLP", "Edge AI",
];

export const offices = [
  {
    country: "USA (Headquarters)",
    address: "701 John Fitch Boulevard, South Windsor, Connecticut 06074, USA",
    phone: "+1 (860) 247-1400",
  },
  {
    country: "India — Bengaluru",
    address: "Brigade Tech Park, K.R Puram Hobli, Whitefield Road, Bengaluru 560066, IN",
    phone: "+91 80 4718 5000",
  },
  {
    country: "India — Noida",
    address: "Trapezoid IT Park, Sector 62, Noida, Uttar Pradesh 201309, IN",
    phone: "+91 120 424 6000",
  },
];

export const footerLinks = {
  Services: ["AI Development", "Custom Software", "Mobile Apps", "DevOps", "Cybersecurity"],
  Company: ["About Us", "Case Studies", "Careers", "Newsroom", "Contact"],
  Resources: ["Blog", "Webinars", "Whitepapers", "Guides", "Support"],
};

// ---- Service detail pages (single source for the /services listing + detail) ----
export const serviceDetails = [
  {
    slug: "ai-development",
    icon: "Brain",
    title: "AI Development & Consulting",
    short: "Automate tasks, deliver smarter experiences, and make better decisions with AI-driven products.",
    tagline: "Intelligent products built with machine learning, NLP, and computer vision.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxBSSUyMGlubm92YXRpb258ZW58MHx8fHwxNzg4NTMwNzgxfDA&ixlib=rb-4.1.0&q=85",
    overview: [
      "We embed artificial intelligence at the core of your product — from strategy and data readiness to model development, deployment, and continuous learning.",
      "Our teams ship production-grade AI: recommendation engines, predictive analytics, chatbots, document intelligence, and autonomous agents that create measurable business value.",
    ],
    capabilities: [
      { title: "ML & Predictive Models", desc: "Custom models for forecasting, scoring, and personalization tuned to your data." },
      { title: "Generative AI", desc: "LLM apps, RAG pipelines, and content generation integrated into your workflows." },
      { title: "Computer Vision", desc: "Detection, OCR, and quality inspection for real-world visual data." },
      { title: "AI Agents", desc: "Autonomous agents that automate multi-step tasks with human-in-the-loop control." },
    ],
    outcomes: ["Faster decisions", "Lower operating cost", "Higher engagement", "New revenue streams"],
    tech: ["Python", "PyTorch", "TensorFlow", "LangChain", "OpenAI", "Vector DBs"],
  },
  {
    slug: "digital-engineering",
    icon: "Cpu",
    title: "Digital Engineering",
    short: "Modernize legacy systems and build secure, next-gen architectures with AI, cloud, and microservices.",
    tagline: "Future-proof platforms engineered for scale, speed, and security.",
    image: "https://images.pexels.com/photos/5833779/pexels-photo-5833779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    overview: [
      "We re-architect legacy monoliths into modular, cloud-native platforms that are easier to evolve, cheaper to run, and ready for AI.",
      "From discovery to delivery, we bring product thinking and platform engineering together to reduce risk and accelerate outcomes.",
    ],
    capabilities: [
      { title: "Legacy Modernization", desc: "Incremental migration paths that de-risk large re-platforming efforts." },
      { title: "Microservices", desc: "Domain-driven services with clean APIs and independent deployability." },
      { title: "Platform Engineering", desc: "Golden paths, observability, and internal developer platforms." },
      { title: "System Integration", desc: "Connect ERPs, CRMs, and third-party systems into one fabric." },
    ],
    outcomes: ["45% more efficient ops", "Lower maintenance cost", "Faster releases", "Improved reliability"],
    tech: ["Node.js", "Java", ".NET", "Kubernetes", "Kafka", "GraphQL"],
  },
  {
    slug: "custom-software",
    icon: "Code2",
    title: "Custom Software Development",
    short: "Tailored software with AI-powered features that solve complex challenges across platforms.",
    tagline: "Bespoke products engineered around your business, not the other way around.",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHxzb2Z0d2FyZSUyMHRlYW18ZW58MHx8fHwxNzg4NTMwNzgxfDA&ixlib=rb-4.1.0&q=85",
    overview: [
      "We design and build web and enterprise applications end-to-end — from UX and architecture to delivery, QA, and ongoing evolution.",
      "Every build is instrumented with analytics and automation so your product keeps getting smarter after launch.",
    ],
    capabilities: [
      { title: "Product Design", desc: "Research-led UX and design systems that convert and scale." },
      { title: "Web & Enterprise Apps", desc: "Robust front-ends and APIs built for performance." },
      { title: "Automation", desc: "Workflow automation and integrations that remove manual work." },
      { title: "QA & Testing", desc: "Automated test coverage baked into the delivery pipeline." },
    ],
    outcomes: ["Faster time to market", "Higher conversion", "Fewer defects", "Lower TCO"],
    tech: ["React", "Next.js", "TypeScript", "Python", "PostgreSQL", "AWS"],
  },
  {
    slug: "mobile-app",
    icon: "Smartphone",
    title: "Mobile App Development",
    short: "Cross-platform apps with AI-driven features and personalized, user-centric experiences.",
    tagline: "Native-quality mobile experiences on iOS, Android, and everything between.",
    image: "https://images.unsplash.com/photo-1551650975-87deedd944c3",
    overview: [
      "We craft mobile products that feel effortless — fast, accessible, and delightful — with AI features like voice, prediction, and personalization.",
      "One codebase, every platform: we balance speed of delivery with the polish users expect.",
    ],
    capabilities: [
      { title: "Cross-Platform", desc: "React Native & Flutter apps from a single, maintainable codebase." },
      { title: "Native Modules", desc: "Deep platform integrations when performance matters." },
      { title: "Offline & Sync", desc: "Resilient experiences that work with poor connectivity." },
      { title: "App Analytics", desc: "Behavioral insight to guide the roadmap after launch." },
    ],
    outcomes: ["60% higher completion", "Better retention", "5-star experiences", "Faster shipping"],
    tech: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "GraphQL"],
  },
  {
    slug: "devops",
    icon: "Infinity",
    title: "DevOps & Cloud Automation",
    short: "Automate, scale, and modernize engineering to accelerate delivery and improve quality.",
    tagline: "Ship faster and sleep better with automated, observable delivery pipelines.",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab",
    overview: [
      "We build the automation backbone — CI/CD, infrastructure as code, and observability — so your teams release with confidence and speed.",
      "From cloud cost optimization to zero-downtime deployments, we make reliability a default, not an afterthought.",
    ],
    capabilities: [
      { title: "CI/CD Pipelines", desc: "Automated build, test, and release across environments." },
      { title: "Infrastructure as Code", desc: "Reproducible, version-controlled infrastructure with Terraform." },
      { title: "Observability", desc: "Metrics, logs, and tracing for fast incident response." },
      { title: "Cost & Scaling", desc: "Autoscaling and FinOps to keep cloud spend under control." },
    ],
    outcomes: ["Faster deployments", "99.9% uptime", "Lower cloud spend", "Fewer incidents"],
    tech: ["Docker", "Kubernetes", "Terraform", "Ansible", "GitHub Actions", "Prometheus"],
  },
  {
    slug: "it-staffing",
    icon: "Users",
    title: "IT Staff Augmentation",
    short: "Onboard the top 3% of pre-vetted talent within 48 hours to scale your team.",
    tagline: "Elite engineers, designers, and AI specialists — on demand.",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHxzb2Z0d2FyZSUyMHRlYW18ZW58MHx8fHwxNzg4NTMwNzgxfDA&ixlib=rb-4.1.0&q=85",
    overview: [
      "Scale your team with flexibility and speed. We match you with pre-vetted specialists who plug straight into your workflow and culture.",
      "From a single senior engineer to a full squad, you get expertise without the overhead of long hiring cycles.",
    ],
    capabilities: [
      { title: "48-Hour Onboarding", desc: "Access the top 3% of vetted talent, fast." },
      { title: "Dedicated Squads", desc: "Cross-functional teams that own outcomes end-to-end." },
      { title: "Flexible Engagement", desc: "Scale up or down as your roadmap changes." },
      { title: "Time-Zone Aligned", desc: "Global talent that overlaps with your working hours." },
    ],
    outcomes: ["Hire in days", "No overhead", "Proven talent", "Flexible scaling"],
    tech: ["Full-Stack", "AI/ML", "Cloud", "Mobile", "Data", "QA"],
  },
  {
    slug: "cybersecurity",
    icon: "ShieldCheck",
    title: "Cybersecurity",
    short: "Enhance your security posture, mitigate risks, and safeguard critical business assets.",
    tagline: "Protect what matters with proactive, tailored security engineering.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    overview: [
      "We help you find and fix risk before attackers do — with assessments, secure architecture, and continuous monitoring built into your SDLC.",
      "Security is engineered in, not bolted on: compliance-ready controls that protect data, customers, and reputation.",
    ],
    capabilities: [
      { title: "Threat Assessment", desc: "Pen testing and audits that surface real, exploitable risk." },
      { title: "Secure SDLC", desc: "Shift-left security across the development lifecycle." },
      { title: "Identity & Access", desc: "Zero-trust IAM, SSO, and least-privilege by design." },
      { title: "Monitoring & Response", desc: "24/7 detection and rapid incident response." },
    ],
    outcomes: ["Reduced risk", "Compliance-ready", "Data protected", "Customer trust"],
    tech: ["Zero Trust", "SIEM", "OWASP", "IAM", "Encryption", "Cloud Security"],
  },
  {
    slug: "cloud",
    icon: "Cloud",
    title: "Cloud Consulting",
    short: "Scalable cloud consulting with seamless integration, optimized performance, and secure access.",
    tagline: "Cloud strategy and delivery that unlocks scale and savings.",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    overview: [
      "We help you migrate, modernize, and operate on the cloud with confidence — balancing performance, security, and cost from day one.",
      "Whether AWS, Azure, or GCP, we design landing zones and platforms that your teams can build on for years.",
    ],
    capabilities: [
      { title: "Cloud Migration", desc: "Assess, plan, and migrate workloads with minimal disruption." },
      { title: "Landing Zones", desc: "Secure, well-architected foundations across accounts." },
      { title: "Serverless & Containers", desc: "Right-sized compute for every workload." },
      { title: "Cost Optimization", desc: "Continuous FinOps to eliminate waste." },
    ],
    outcomes: ["Elastic scale", "Lower cost", "Secure by default", "Global reach"],
    tech: ["AWS", "Azure", "GCP", "Terraform", "Kubernetes", "Serverless"],
  },
];

// ---- About page ----
export const about = {
  intro: "We are a one-in-a-billion digital transformation partner — engineering AI-powered software that helps ambitious companies build smarter, scale faster, and lead their markets.",
  story: [
    "Infinity Solutions was founded on a simple belief: great engineering is a competitive advantage, not a cost center. As a proud partner of VLink, we bring together elite global talent and battle-tested delivery to help enterprises turn bold ideas into working software.",
    "From Fortune 250 companies to fast-growing challengers, we've delivered 300+ projects across 30+ industries — always with the same relentless focus on measurable outcomes.",
  ],
  mission: "To engineer intelligent, scalable software that creates lasting value for our clients and the people they serve.",
  values: [
    { icon: "Target", title: "Outcomes Over Output", desc: "We measure success by the business results we create, not the hours we log." },
    { icon: "Users", title: "Elite Talent", desc: "We work with the top 3% of engineers, designers, and AI specialists." },
    { icon: "ShieldCheck", title: "Built to Trust", desc: "Security, quality, and reliability are engineered in from day one." },
    { icon: "Zap", title: "Relentless Speed", desc: "We move fast without breaking things — with automation and discipline." },
    { icon: "Infinity", title: "Long-Term Partners", desc: "We invest in relationships that last far beyond a single project." },
    { icon: "Sparkles", title: "AI-First", desc: "We embed intelligence at the core of everything we build." },
  ],
  leaders: [
    { name: "Rajiv Menon", role: "Chief Executive Officer", bio: "20+ years scaling global engineering teams and digital businesses." },
    { name: "Sara Whitman", role: "Chief Technology Officer", bio: "Cloud-native architect and AI platform builder for enterprise scale." },
    { name: "Daniel Okafor", role: "VP, Engineering", bio: "Leads delivery across 30+ industries with a bias for quality." },
    { name: "Priya Nair", role: "VP, AI & Data", bio: "Turns messy data into production-grade machine intelligence." },
    { name: "Marcus Lee", role: "Head of Design", bio: "Crafts research-led experiences that convert and delight." },
    { name: "Elena Rossi", role: "Head of Delivery", bio: "Keeps every engagement on time, on budget, and on outcome." },
  ],
  milestones: [
    { year: "2011", text: "Founded with a mission to make engineering a competitive edge." },
    { year: "2016", text: "Crossed 100 successful projects across multiple industries." },
    { year: "2020", text: "Opened global delivery centers and scaled to 650+ engineers." },
    { year: "2024", text: "Went AI-first, embedding intelligence across every practice." },
  ],
};
